import {
    Box,
    Paper,
    Typography,
    Container,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent
} from '@mui/lab';
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import PropTypes from 'prop-types';

const OurJourney = ({ language = 'EN' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    // Language content
    const content = {
        EN: {
            title: "Our Journey",
            milestones: [
                {
                    year: "2017",
                    event: "• Founded\n• Organized online short courses called 'Beauty CEO profit is king'\n• First study trip to GuangZhou, China with 20 beauty entrepreneurs"
                },
                {
                    year: "2018",
                    event: "• Organized 'Beauty Learning Space' in multiple locations\n• First beauty business management course with 130 attendees"
                },
                {
                    year: "2019",
                    event: "• Awarded 'Consumers' Recommended Award'\n• Second course with 250 attendees\n• Rebranded to Unicorn Beauty Business School"
                },
                {
                    year: "2020",
                    event: "• International Beauty Certified Institute approved\n• Key collaborations established\n• Appointed as Chairman of Fashion & Beauty Development Board"
                },
                {
                    year: "2021",
                    event: "• Partnership with University Malaya\n• Established 8 branches\n• Developed Online Video Education with 500+ videos"
                },
                {
                    year: '2022',
                    event: "• Collected 20,000+ data points\n• Started giving business management talks"
                },
                {
                    year: '2023',
                    event: "• International Accreditation\n• Global recognition achieved"
                },
                {
                    year: '2024',
                    event: "• Charity sharings (20+ offline, 30+ online)\n• Created BBM syllabus with University Malaya"
                },
            ]
        },
        BM: {
            title: "Perjalanan Kami",
            milestones: [
                {
                    year: "2017",
                    event: "• Diasaskan\n• Menganjurkan kursus pendek dalam talian bernama 'Beauty CEO profit is king'\n• Perjalanan kajian pertama ke GuangZhou, China dengan 20 usahawan kecantikan"
                },
                {
                    year: "2018",
                    event: "• Menganjurkan 'Beauty Learning Space' di pelbagai lokasi\n• Kursus pengurusan perniagaan kecantikan pertama dengan 130 peserta"
                },
                {
                    year: "2019",
                    event: "• Dianugerahkan 'Anugerah Disyorkan Pengguna'\n• Kursus kedua dengan 250 peserta\n• Dinamakan semula sebagai Unicorn Beauty Business School"
                },
                {
                    year: "2020",
                    event: "• Institut Sijil Kecantikan Antarabangsa diluluskan\n• Kolaborasi utama ditubuhkan\n• Dilantik sebagai Pengerusi Lembaga Pembangunan Fesyen & Kecantikan"
                },
                {
                    year: "2021",
                    event: "• Perkongsian dengan Universiti Malaya\n• Menubuhkan 8 cawangan\n• Membangunkan Pendidikan Video Dalam Talian dengan 500+ video"
                },
                {
                    year: '2022',
                    event: "• Mengumpul 20,000+ titik data\n• Mula memberi ceramah pengurusan perniagaan"
                },
                {
                    year: '2023',
                    event: "• Akreditasi Antarabangsa\n• Pengiktirafan global dicapai"
                },
                {
                    year: '2024',
                    event: "• Perkongsian amal (20+ luar talian, 30+ dalam talian)\n• Mencipta sukatan pelajaran BBM dengan Universiti Malaya"
                },
            ]
        },
        CN: {
            title: "我们的历程",
            milestones: [
                {
                    year: "2017",
                    event: "• 成立\n• 组织名为'美业CEO利润为王'的在线短期课程\n• 首次组织20位美业企业家前往中国广州学习考察"
                },
                {
                    year: "2018",
                    event: "• 在多个地点组织'美业学习空间'\n• 首届美业商业管理课程，130人参加"
                },
                {
                    year: "2019",
                    event: "• 荣获'消费者推荐奖'\n• 第二届课程有250人参加\n• 更名为Unicorn Beauty Business School"
                },
                {
                    year: "2020",
                    event: "• 获得国际美业认证机构批准\n• 建立关键合作关系\n• 被任命为时尚与美业发展委员会主席"
                },
                {
                    year: "2021",
                    event: "• 与马来亚大学建立合作关系\n• 设立8个分支机构\n• 开发500+在线视频教育课程"
                },
                {
                    year: '2022',
                    event: "• 收集20,000+数据点\n• 开始举办商业管理讲座"
                },
                {
                    year: '2023',
                    event: "• 获得国际认证\n• 实现全球认可"
                },
                {
                    year: '2024',
                    event: "• 慈善分享(线下20+场，线上30+场)\n• 与马来亚大学共同创建BBM教学大纲"
                },
            ]
        }
    };

    const currentContent = content[language] || content.EN;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: {
            opacity: 0,
            y: 30
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    if (isMobile) {
        return (
            <Box
                id="timeline-section"
                ref={ref}
                sx={{
                    py: 4,
                    px: 2,
                    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.custom.lightBackground} 100%)`,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Container disableGutters maxWidth={false}>
                    <motion.div
                        initial="hidden"
                        animate={controls}
                        variants={containerVariants}
                    >
                        <Typography
                            variant="h4"
                            align="center"
                            gutterBottom
                            component={motion.div}
                            variants={{
                                hidden: { opacity: 0, y: -20 },
                                visible: {
                                    opacity: 1,
                                    y: 0,
                                    transition: { duration: 0.6 }
                                }
                            }}
                            sx={{
                                mb: 4,
                                fontWeight: 800,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            {currentContent.title}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {currentContent.milestones.map((item, index) => (
                                <motion.div
                                    key={index}
                                    variants={itemVariants}
                                >
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            background: theme.palette.background.paper,
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            borderLeft: `4px solid ${theme.palette.primary.main}`
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                            sx={{
                                                fontWeight: 'bold',
                                                mb: 1.5,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: theme.palette.primary.contrastText,
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'bold',
                                                    mr: 2
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                            {item.year}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: 'pre-line',
                                                textAlign: 'left',
                                                lineHeight: 1.7,
                                                pl: 6
                                            }}
                                        >
                                            {item.event}
                                        </Typography>
                                    </Paper>
                                </motion.div>
                            ))}
                        </Box>
                    </motion.div>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            id="timeline-section"
            ref={ref}
            sx={{
                py: isMobile ? 6 : 12,
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.custom.lightBackground} 100%)`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100%',
                    background: `radial-gradient(circle at 20% 50%, ${theme.palette.primary.light}20 0%, transparent 30%)`,
                    zIndex: 0
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    background: `radial-gradient(circle at 80% 70%, ${theme.palette.secondary.light}15 0%, transparent 40%)`,
                    zIndex: 0
                }
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <motion.div
                    initial="hidden"
                    animate={controls}
                    variants={containerVariants}
                >
                    <Typography
                        variant={isMobile ? "h3" : "h2"}
                        align="center"
                        gutterBottom
                        component={motion.div}
                        variants={{
                            hidden: { opacity: 0, y: -40 },
                            visible: {
                                opacity: 1,
                                y: 0,
                                transition: {
                                    duration: 0.8,
                                    ease: [0.17, 0.67, 0.83, 0.67]
                                }
                            }
                        }}
                        sx={{
                            mb: isMobile ? 4 : 8,
                            fontWeight: 800,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 2px 10px rgba(0,0,0,0.05)',
                            position: 'relative',
                            display: 'inline-block',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60%',
                                height: 4,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                borderRadius: 2
                            }
                        }}
                    >
                        {currentContent.title}
                    </Typography>

                    <Timeline
                        position={isMobile ? "right" : "alternate"}
                        sx={{
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 2,
                                height: '100%',
                                background: `linear-gradient(to bottom, ${theme.palette.primary.main}00, ${theme.palette.primary.main}80, ${theme.palette.primary.main}00)`,
                                display: isMobile ? 'none' : 'block'
                            }
                        }}
                    >
                        {currentContent.milestones.map((item, index) => (
                            <TimelineItem key={index} sx={{ minHeight: isMobile ? 120 : 160 }}>
                                <TimelineSeparator>
                                    <motion.div variants={itemVariants}>
                                        <TimelineDot
                                            sx={{
                                                width: isMobile ? 24 : 32,
                                                height: isMobile ? 24 : 32,
                                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                boxShadow: `0 0 0 4px ${theme.palette.background.paper}, 0 0 20px ${theme.palette.primary.light}80`,
                                                p: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: theme.palette.primary.contrastText,
                                                    fontWeight: 'bold',
                                                    fontSize: isMobile ? '0.7rem' : '0.8rem'
                                                }}
                                            >
                                                {item.year}
                                            </Typography>
                                        </TimelineDot>
                                    </motion.div>
                                    {index !== currentContent.milestones.length - 1 && (
                                        <TimelineConnector sx={{
                                            background: `linear-gradient(to bottom, ${theme.palette.primary.light}80, ${theme.palette.secondary.light}80)`,
                                            width: 2,
                                            height: isMobile ? 60 : 80
                                        }} />
                                    )}
                                </TimelineSeparator>
                                <TimelineContent sx={{ py: isMobile ? '12px' : '24px' }}>
                                    <motion.div
                                        custom={index}
                                        variants={itemVariants}
                                    >
                                        <Paper
                                            elevation={isMobile ? 2 : 6}
                                            sx={{
                                                p: isMobile ? 2.5 : 3.5,
                                                borderRadius: 4,
                                                background: `linear-gradient(to bottom right, ${theme.palette.background.paper}, ${theme.palette.custom.lightBackground})`,
                                                backdropFilter: 'blur(4px)',
                                                border: `1px solid ${theme.palette.primary.light}20`,
                                                boxShadow: `0 8px 32px ${theme.palette.primary.light}10`,
                                                transition: 'all 0.4s ease',
                                                '&:hover': !isMobile && {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: `0 12px 40px ${theme.palette.primary.light}20`,
                                                    borderColor: `${theme.palette.primary.light}40`
                                                },
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: 4,
                                                    height: '100%',
                                                    background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                                                }
                                            }}
                                        >
                                            <Typography
                                                variant={isMobile ? "body2" : "body1"}
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                    textAlign: 'left',
                                                    lineHeight: 1.7,
                                                    color: theme.palette.text.primary,
                                                    '&::first-letter': {
                                                        fontWeight: 'bold',
                                                        color: theme.palette.primary.main,
                                                        fontSize: '1.2em'
                                                    }
                                                }}
                                            >
                                                {item.event}
                                            </Typography>
                                        </Paper>
                                    </motion.div>
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </Timeline>
                </motion.div>
            </Container>
        </Box>
    );
};

OurJourney.propTypes = {
    language: PropTypes.oneOf(['EN', 'BM', 'CN'])
};

export default OurJourney;